import { Dispatch, SetStateAction, useMemo } from "react";
import {
  FEE_ALLOCATION_OPTIONS,
  FEE_ASSESSED_TYPE,
} from "../../../../lib/constants";
import { ColumnsType } from "antd/es/table";
import { EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons";
import { IFeeTypeExtended } from "../../../../types/Fees";
import { useButtonHandlers } from "./buttonHandlers";
import { usePermissions } from "../../../../lib/hooks/usePermissions";
import { getFormattedCurrency } from "../../../../lib/currencyFormatter";

type TFeeTableColumnsProps = {
  accountId: string | undefined;
  reloadData: () => void;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  openSideBar: (id: string) => void;
};

export const useGetFeesTableColumns = ({
  accountId,
  reloadData,
  setIsOpen,
  openSideBar,
}: TFeeTableColumnsProps) => {
  const { onDeleteBtnClick } = useButtonHandlers({
    parentId: accountId,
    reloadData,
    setIsOpen,
  });

  const { hasAccessByController } = usePermissions(
    accountId ? "FeeContracts" : "FeeTypes",
  );

  const columns: ColumnsType<IFeeTypeExtended> = useMemo(() => {
    const defaultColumns: ColumnsType<IFeeTypeExtended> = [
      {
        title: accountId ? "Fee Type" : "Fee Type Name",
        dataIndex: "feeType",
        width: `${accountId ? "20" : "22"}%`,
        key: "feeType",
      },
      {
        title: "Allocation",
        dataIndex: "feeAllocation",
        width: "10%",
        key: "feeAllocation",
        render: (value: number) => {
          const finalValue = FEE_ALLOCATION_OPTIONS.find(
            (option) => option.value === value,
          )?.label;

          return finalValue;
        },
      },
      {
        title: "Buyer Fee",
        dataIndex: "buyerFee",
        width: "9%",
        key: "buyerFee",
        align: "right",
        ellipsis: true,
        render: (value: number) => getFormattedCurrency(value),
      },
      {
        title: "Buyer Tax",
        dataIndex: "buyerTax",
        width: "9%",
        key: "buyerTax",
        align: "right",
        ellipsis: true,
        render: (value: number) => getFormattedCurrency(value),
      },
      {
        title: "Seller Fee",
        dataIndex: "sellerFee",
        width: "9%",
        key: "sellerFee",
        align: "right",
        ellipsis: true,
        render: (value: number) => getFormattedCurrency(value),
      },
      {
        title: "Seller Tax",
        dataIndex: "sellerTax",
        width: "9%",
        key: "sellerTax",
        align: "right",
        ellipsis: true,
        render: (value: number) => getFormattedCurrency(value),
      },
      {
        title: "Assessed",
        dataIndex: "feeAssessed",
        width: `${accountId ? "20" : "22"}%`,
        key: "feeAssessed",
        render: (value: number) => {
          const finalValue = FEE_ASSESSED_TYPE.find(
            (type) => type.value === value,
          )?.label;
          const textFormater = value === 0 ? "error" : "";

          return (
            <span className={`${textFormater} elipsis`} title={finalValue}>
              {finalValue}
            </span>
          );
        },
      },
    ];

    if (accountId) {
      defaultColumns.splice(1, 1);

      defaultColumns.push({
        title: "Apply Global Changes",
        dataIndex: "globalAdjustmentAllowed",
        key: "globalAdjustmentAllowed",
        width: "14%",
        render: (value: boolean) => (value ? "Yes" : "No"),
      });
    }

    const menuKeys: string[] = [];

    hasAccessByController("Modify") && menuKeys.push("1");

    hasAccessByController("Delete") && menuKeys.push("2");

    menuKeys.length > 0 &&
      defaultColumns.push({
        title: "",
        width: "10%",
        key: "actions",
        render: (_: any, record: IFeeTypeExtended) => {
          const { id, references } = record;

          const iconProps: any = {
            className: "ant-table-btn mr-1 logo-cursor",
            onClick: () => onDeleteBtnClick(id, references),
          };

          return (
            <div
              className="w-100 h-100 d-flex justify-end ant-table-actions"
              onClick={(e) => e.stopPropagation()}
            >
              {menuKeys.includes("1") && (
                <EditOutlined
                  className="ant-table-btn logo-cursor"
                  onClick={() => openSideBar(id)}
                />
              )}
              {menuKeys.includes("2") && (
                <>
                  {accountId ? (
                    <CloseOutlined {...iconProps} />
                  ) : (
                    <DeleteOutlined {...iconProps} />
                  )}
                </>
              )}
            </div>
          );
        },
      });

    return defaultColumns;
  }, [accountId, hasAccessByController, onDeleteBtnClick, openSideBar]);

  return { columns };
};
